// js/AdminBoard/CustomerSupportBoard/DeviceDetail/Tabs/DeviceFeaturesTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import moment from 'moment';
import toast from "react-hot-toast";
import {
  GetFeaturesByDevice,
  CreateFeatureTrial,
  UpdateFeatureTrial,
} from "js/Api/index";

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function toDateTimeLocalString(date) {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const MM = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
}

function DeviceFeaturesTab({ device }) {
  const [loading, setLoading] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [deviceEntry, setDeviceEntry] = useState(null);

  const [creating, setCreating] = useState(false);
  const [selectedFeatureId, setSelectedFeatureId] = useState("");
  const [validFromLocal, setValidFromLocal] = useState("");
  const [validToLocal, setValidToLocal] = useState("");

  const [editingRowId, setEditingRowId] = useState(null);
  const [editValidFromLocal, setEditValidFromLocal] = useState("");
  const [editValidToLocal, setEditValidToLocal] = useState("");
  const [updating, setUpdating] = useState(false);

  const deviceId = device?.id;

  const loadData = async () => {
    if (!deviceId) return;
    setLoading(true);
    try {
      const res = await GetFeaturesByDevice(deviceId);
      const data = res.data || {};

      const categories = Array.isArray(data.features) ? data.features : [];
      const deviceFeatures = Array.isArray(data.device_features)
        ? data.device_features
        : [];

      setAllCategories(categories);

      const current = deviceFeatures.find((d) => d.id === deviceId) || null;
      setDeviceEntry(current);

      if (!validFromLocal || !validToLocal) {
        const now = new Date();
        const oneYearLater = new Date();
        oneYearLater.setFullYear(now.getFullYear() + 1);
        setValidFromLocal(toDateTimeLocalString(now));
        setValidToLocal(toDateTimeLocalString(oneYearLater));
      }
    } catch (err) {
      console.error("Failed to load device features:", err);
      toast.error("Failed to load device features");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!deviceId) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const flatFeatures = useMemo(() => {
    const list = [];
    (allCategories || []).forEach((cat) => {
      (cat.features || []).forEach((f) => {
        list.push({
          ...f,
          categoryName: cat.categoryName,
        });
      });
    });
    return list;
  }, [allCategories]);

  const featureMap = useMemo(() => {
    const map = {};
    flatFeatures.forEach((f) => {
      map[f.id] = f;
    });
    return map;
  }, [flatFeatures]);

  const currentPurchases = useMemo(() => {
    if (!deviceEntry || !Array.isArray(deviceEntry.feature_purchase)) {
      return [];
    }
    return [...deviceEntry.feature_purchase].sort((a, b) => {
      const fa = featureMap[a.featureId]?.featureName || "";
      const fb = featureMap[b.featureId]?.featureName || "";
      return fa.localeCompare(fb);
    });
  }, [deviceEntry, featureMap]);

  const activeFeatureIds = useMemo(() => {
    const set = new Set();
    currentPurchases.forEach((p) => {
      if (p.featureId) set.add(p.featureId);
    });
    return set;
  }, [currentPurchases]);

  const availableFeatures = useMemo(() => {
    return flatFeatures.filter((f) => !activeFeatureIds.has(f.id));
  }, [flatFeatures, activeFeatureIds]);

  const handleCreate = async () => {
    if (!deviceId) {
      toast.error("Invalid device");
      return;
    }
    if (!selectedFeatureId) {
      toast.error("Please select a feature");
      return;
    }
    if (!validFromLocal || !validToLocal) {
      toast.error("Please select valid from / to dates");
      return;
    }

    let fromDate = new Date(validFromLocal);
    let toDate = new Date(validToLocal);

    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
      toast.error("Invalid date format");
      return;
    }

    if (fromDate >= toDate) {
      toast.error("Valid To must be after Valid From");
      return;
    }

    fromDate = moment.utc(fromDate);
    toDate = moment.utc(toDate);

    const payload = {
      deviceId,
      featureId: selectedFeatureId,
      validFrom: fromDate,
      validTo: toDate,
    };

    setCreating(true);
    try {
      await CreateFeatureTrial(payload);
      toast.success("Feature added to device");
      await loadData();
      setSelectedFeatureId("");
    } catch (err) {
      console.error("Failed to create feature trail:", err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to add feature";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (purchase) => {
    setEditingRowId(purchase.id);
    setEditValidFromLocal(toDateTimeLocalString(purchase.validFrom));
    setEditValidToLocal(toDateTimeLocalString(purchase.validTo));
  };

  const cancelEdit = () => {
    setEditingRowId(null);
    setEditValidFromLocal("");
    setEditValidToLocal("");
  };

  const handleUpdate = async () => {
    if (!deviceId) {
      toast.error("Invalid device");
      return;
    }
    const row = currentPurchases.find((p) => p.id === editingRowId);
    if (!row) {
      toast.error("Invalid feature record");
      return;
    }
    if (!editValidFromLocal || !editValidToLocal) {
      toast.error("Please select valid from / to dates");
      return;
    }

    let fromDate = new Date(editValidFromLocal);
    let toDate = new Date(editValidToLocal);

    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
      toast.error("Invalid date format");
      return;
    }

    if (fromDate >= toDate) {
      toast.error("Valid To must be after Valid From");
      return;
    }

    fromDate = moment.utc(fromDate);
    toDate = moment.utc(toDate);

    const payload = {
      deviceId,
      featureId: row.featureId,
      validFrom: fromDate,
      validTo: toDate,
    };

    setUpdating(true);
    try {
      await UpdateFeatureTrial(payload);
      toast.success("Feature dates updated");
      cancelEdit();
      await loadData();
    } catch (err) {
      console.error("Failed to update feature trail:", err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to update feature";
      toast.error(msg);
    } finally {
      setUpdating(false);
    }
  };

  if (!deviceId) {
    return (
      <div className="padding_infor_info" style={{ paddingTop: 20 }}>
        <p>No device selected.</p>
      </div>
    );
  }

  return (
    <div className="padding_infor_info" style={{ paddingTop: 20 }}>
      <h5 style={{ marginTop: 0, marginBottom: 15 }}>Device Features</h5>

      {loading ? (
        <div style={{ padding: "20px 0", textAlign: "center" }}>
          <i
            className="fa fa-spinner fa-spin"
            style={{ fontSize: "24px", marginRight: 8, color: "#007bff" }}
          />
          <p style={{ marginTop: 10, color: "#666" }}>
            Loading device features...
          </p>
        </div>
      ) : (
        <>
          <div
            style={{
              marginBottom: 24,
              border: "1px solid #eee",
              borderRadius: 6,
              backgroundColor: "#fafafa",
              padding: 15,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 10,
                alignItems: "center",
              }}
            >
              <span style={{ fontWeight: 600 }}>Current active features</span>
              <span style={{ fontSize: 12, color: "#666" }}>
                {currentPurchases.length} feature
                {currentPurchases.length === 1 ? "" : "s"}
              </span>
            </div>

            {currentPurchases.length === 0 ? (
              <p style={{ margin: 0, color: "#777" }}>
                This device currently has no active features.
              </p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="table table-striped table-sm">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Category</th>
                      <th>Valid From</th>
                      <th>Valid To</th>
                      <th>Created At</th>
                      <th style={{ width: 140 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPurchases.map((p) => {
                      const feat = featureMap[p.featureId] || {};
                      const isEditing = editingRowId === p.id;

                      return (
                        <tr key={p.id}>
                          <td>
                            <div style={{ fontWeight: 500 }}>
                              {feat.featureName || "Unknown feature"}
                            </div>
                            {feat.friendlyName && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#888",
                                  marginTop: 2,
                                }}
                              >
                                {feat.friendlyName}
                              </div>
                            )}
                          </td>
                          <td>{feat.categoryName || "-"}</td>
                          <td>
                            {isEditing ? (
                              <input
                                type="datetime-local"
                                className="form-control"
                                value={editValidFromLocal}
                                onChange={(e) =>
                                  setEditValidFromLocal(e.target.value)
                                }
                              />
                            ) : (
                              formatDateTime(p.validFrom)
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="datetime-local"
                                className="form-control"
                                value={editValidToLocal}
                                onChange={(e) =>
                                  setEditValidToLocal(e.target.value)
                                }
                              />
                            ) : (
                              formatDateTime(p.validTo)
                            )}
                          </td>
                          <td>{formatDateTime(p.createdAt)}</td>
                          <td>
                            {isEditing ? (
                              <div style={{ display: "flex", gap: 6 }}>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={handleUpdate}
                                  disabled={updating}
                                >
                                  {updating ? "Saving..." : "Save"}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary"
                                  onClick={cancelEdit}
                                  disabled={updating}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => startEdit(p)}
                              >
                                Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div
            style={{
              border: "1px solid #eee",
              borderRadius: 6,
              backgroundColor: "#fafafa",
              padding: 15,
            }}
          >
            <h6 style={{ marginTop: 0, marginBottom: 12 }}>Add Feature</h6>

            {availableFeatures.length === 0 ? (
              <p style={{ margin: 0, color: "#777" }}>
                All available features are already active on this device.
              </p>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "minmax(220px, 2fr) minmax(160px, 1fr) minmax(160px, 1fr)",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <label
                      style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}
                    >
                      Feature
                    </label>
                    <select
                      className="form-control"
                      value={selectedFeatureId}
                      onChange={(e) => setSelectedFeatureId(e.target.value)}
                    >
                      <option value="">-- Select a feature --</option>
                      {availableFeatures.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.featureName}{" "}
                          {f.categoryName ? `(${f.categoryName})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}
                    >
                      Valid From
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={validFromLocal}
                      onChange={(e) => setValidFromLocal(e.target.value)}
                    />
                  </div>

                  <div>
                    <label
                      style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}
                    >
                      Valid To
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={validToLocal}
                      onChange={(e) => setValidToLocal(e.target.value)}
                    />
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 16,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                  }}
                >
                  <button
                    type="button"
                    className="btn cur-p btn-primary"
                    onClick={handleCreate}
                    disabled={creating}
                  >
                    {creating ? "Adding..." : "Add Feature"}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default DeviceFeaturesTab;
